import React, { useRef, useState, useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Select, Button, DatePicker, Dropdown, Tooltip, MenuProps, InputRef } from 'antd';
import { isValidPhoneNumber } from 'react-phone-number-input';
import * as nhsValidator from 'nhs-number-validator';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import useIsNHS from '@hooks/useIsNHS';
import PhoneInput from '@components/FormInputs/PhoneInput';
import Flex from '@components/UI/Flex';
import { sanitiseValueFromAngleBrackets } from 'vl-common/src/utils';
import { Moment } from 'moment';
import { successModal } from 'vl-common/src/components/Modals';

const { Search } = Input;

type DependantRequest = {
  addline1: string;
  addline2: string;
  addline3: string;
  addline4: string;
  country: string;
  county: string;
  date_of_birth: string;
  email: string;
  first_name: string;
  last_name: string;
  membership_no: string;
  patient_number: string;
  postcode: string;
  proxy_id: string;
  region: string;
  sex: string;
  tel_mobile_no: string;
  tel_other_no: string;
  title: string;
};

type DependantFormFields = {
  address_line_1: string;
  address_line_2: string;
  address_line_3: string;
  country: string;
  county: string;
  date_of_birth: Moment;
  email_address: string;
  first_name: string;
  last_name: string;
  patient_number: string;
  postcode: string;
  primary_contact_number: string;
  secondary_contact_number: string;
  sex: string;
  title: string;
  town_or_city: string;
  proxy_id: string;
};

const AddDependantForm = ({
  onCancel,
  user,
  updateProxyRequestDetails,
  setUpdateProxyRequestDetails,
  titleOptions,
  sexOptions
}: {
  onCancel: () => void;
  user: { user_guid: string };
  updateProxyRequestDetails: DependantRequest;
  setUpdateProxyRequestDetails: (value: DependantRequest) => void;
  titleOptions: { title: string }[];
  sexOptions: { sex_code: string; sex_desc: string }[];
}) => {
  const { t } = useTranslation('common');
  const [form] = Form.useForm<DependantFormFields>();
  const isNHS = useIsNHS();
  const dispatch = useDispatch();
  const postcodeSearchRef = useRef<InputRef>(null);

  const [postcodeOptions, setPostcodeOptions] = useState<
    {
      key: string;
      label: string;
      line_1: string;
      line_2: string;
      line_3: string;
      post_town: string;
      county: string;
      country: string;
    }[]
  >([]);
  const [fetchingPostcodeOptions, setFetchingPostcodeOptions] = useState(false);

  const errorModal = (content: React.ReactNode) => {
    errorModal({ content });
  };

  useEffect(() => {
    if (updateProxyRequestDetails) {
      form.setFieldsValue({
        address_line_1: updateProxyRequestDetails?.addline1,
        address_line_2: updateProxyRequestDetails?.addline2,
        address_line_3: updateProxyRequestDetails?.addline3,
        country: updateProxyRequestDetails?.country,
        county: updateProxyRequestDetails?.region,
        date_of_birth: null,
        email_address: updateProxyRequestDetails?.email,
        first_name: updateProxyRequestDetails?.first_name,
        last_name: updateProxyRequestDetails?.last_name,
        patient_number: null,
        postcode: updateProxyRequestDetails?.postcode,
        primary_contact_number: updateProxyRequestDetails?.tel_mobile_no
          ? `+${updateProxyRequestDetails?.tel_mobile_no}`
          : '',
        secondary_contact_number: updateProxyRequestDetails?.tel_other_no
          ? `+${updateProxyRequestDetails?.tel_other_no}`
          : '',
        sex: updateProxyRequestDetails?.sex,
        title: updateProxyRequestDetails?.title,
        town_or_city: updateProxyRequestDetails?.addline4
      });
    }
  }, [updateProxyRequestDetails]);

  const onFinish = async (values: any) => {
    await submitProxyRequest(
      {
        user_guid: user?.user_guid,
        proxy_id: updateProxyRequestDetails?.proxy_id || null,
        submit: true,
        membership_no: values?.patient_number,
        title: values?.title,
        first_name: values?.first_name,
        last_name: values?.last_name,
        email: values?.email_address,
        dob: values?.date_of_birth?.format('YYYY-MM-DD'),
        sex: values?.sex,
        tel_mobile: values?.primary_contact_number ? values?.primary_contact_number.replace('+', '') : null,
        tel_other: values?.secondary_contact_number ? values?.secondary_contact_number.replace('+', '') : null,
        addline1: values?.address_line_1,
        addline2: values?.address_line_2,
        addline3: values?.address_line_3,
        addline4: values?.town_or_city,
        region: values?.county,
        postcode: values?.postcode,
        country: values?.country
      },
      'Successfully requested proxy'
    );
  };

  const submitProxyRequest = async (body: unknown, message: string) => {
    await dispatch(actions.submitProxyRequests(body), false)
      ?.then((res) =>
        res?.proxy_id
          ? successModal({
              content: message,
              onOk() {
                closeModal();
              }
            })
          : errorModal('An unexpected error occured. Please check the entered details.')
      )
      .catch(() => errorModal('An unexpected error occured. Please check the entered details.'));
  };

  const closeModal = () => {
    setUpdateProxyRequestDetails(null);
    form.resetFields();
    onCancel();
  };

  const formatAddress = (addr: string[]) => {
    const [addline1, addline2, addline3] = addr;
    return [addline1, addline2, addline3].filter(Boolean).join(', ');
  };

  const searchPostcode = async (postcode: string) => {
    if (postcode && postcode?.length > 4) {
      if (!fetchingPostcodeOptions) {
        setFetchingPostcodeOptions(true);
        const addresses = await dispatch(actions.fetchAddresses(postcode));
        if (addresses.result) {
          setPostcodeOptions(
            addresses.result.map((option) => ({
              ...option,
              key: formatAddress([option.line_1, option.line_2, option.line_3, option.post_town, option.county]),
              label: formatAddress([option.line_1, option.line_2, option.line_3, option.post_town, option.county])
            }))
          );
          /* @ts-ignore */
          postcodeSearchRef?.current?.focus();
        }
        setFetchingPostcodeOptions(false);
      }
    }
  };

  const saveRequest = async () => {
    await form.validateFields().then(async (fields) => {
      await submitProxyRequest(
        {
          user_guid: user?.user_guid,
          proxy_id: fields?.proxy_id,
          submit: false,
          membership_no: fields?.patient_number || null,
          title: fields?.title || null,
          first_name: fields?.first_name || null,
          last_name: fields?.last_name || null,
          email: fields?.email_address || null,
          dob: fields?.date_of_birth?.format('YYYY-MM-DD') || null,
          sex: fields?.sex || null,
          tel_mobile: fields?.primary_contact_number?.replace('+', '') || null,
          tel_other: fields?.secondary_contact_number?.replace('+', '') || null,
          addline1: fields?.address_line_1 || null,
          addline2: fields?.address_line_2 || null,
          addline3: fields?.address_line_3 || null,
          addline4: fields?.town_or_city || null,
          region: fields?.county || null,
          postcode: fields?.postcode || null,
          country: fields?.country || null
        },
        'Successfully saved details'
      );
    });
  };

  const addressDropdownMenu = () => {
    const options: MenuProps['items'] = [];

    postcodeOptions?.forEach((val, index) => {
      options.push({ key: index, label: val?.label });
    });

    return options;
  };

  const handleDropdownClick: ComponentProps<typeof Dropdown>['menu']['onClick'] = (e) => {
    const selectedAddress = postcodeOptions[parseInt(e.key)];

    form.setFieldsValue({
      address_line_1: selectedAddress?.line_1,
      address_line_2: selectedAddress?.line_2,
      address_line_3: selectedAddress?.line_3,
      town_or_city: selectedAddress?.post_town,
      county: selectedAddress?.county,
      country: selectedAddress?.country
    });
  };

  return (
    <Form
      form={form}
      name="control-hooks"
      onFinish={onFinish}
      validateTrigger="onFinish"
      requiredMark="optional"
      data-testid="add-dependant-form"
      validateMessages={{ required: '${label} is required' }}
      wrapperCol={{ sm: { span: 24 }, md: { span: 16 } }}
      labelCol={{ sm: { span: 24 }, md: { span: 8 } }}
      labelWrap
    >
      <Form.Item name="title" label={t('Title')} rules={[{ required: true }]}>
        <Select
          options={titleOptions?.map((option) => ({
            value: option?.title,
            label: option?.title
          }))}
          data-testid="title-select"
        />
      </Form.Item>

      <Form.Item name="first_name" label={t('First name')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('first_name', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="last_name" label={t('Last name')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('last_name', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="email_address" label={t('Email address')} rules={[{ required: true }]}>
        <Input data-testid="email-address-field" />
      </Form.Item>

      <Form.Item name="sex" label={t('Sex')} rules={[{ required: true }]}>
        <Select
          options={sexOptions?.map((option) => ({
            value: option?.sex_code,
            label: option?.sex_desc
          }))}
          data-testid="sex-select"
        />
      </Form.Item>

      <Form.Item
        name="date_of_birth"
        label={t('DateOfBirth')}
        rules={[
          { required: true },
          () => ({
            validator(_, value) {
              const today = new Date();
              const age = today.getFullYear() - value.year();

              if (age >= 18 && age <= 115) {
                return Promise.resolve();
              }

              return Promise.reject(new Error('The dependant must be between 18 and 115 years old'));
            }
          })
        ]}
      >
        <DatePicker format="DD/MM/YYYY" />
      </Form.Item>

      <Form.Item
        name="primary_contact_number"
        label={t('Primary contact number')}
        rules={[
          { required: true },
          () => ({
            validator(_, value) {
              if (!value || isValidPhoneNumber(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The number submitted is not valid'));
            }
          })
        ]}
      >
        {/* @ts-ignore */}
        <PhoneInput defaultCountry="GB" />
      </Form.Item>

      <Form.Item
        name="secondary_contact_number"
        label={t('Secondary contact number')}
        rules={[
          () => ({
            validator(_, value) {
              if (!value || isValidPhoneNumber(value)) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The number submitted is not valid'));
            }
          })
        ]}
      >
        {/* @ts-ignore */}
        <PhoneInput defaultCountry="GB" />
      </Form.Item>

      <Form.Item
        name="patient_number"
        label={t('Patient number')}
        rules={[
          { required: true },
          () => ({
            validator(_, value) {
              return !isNHS || nhsValidator.validate(value)
                ? Promise.resolve()
                : Promise.reject(Error('Invalid NHS number'));
            }
          })
        ]}
      >
        <Input
          onChange={(e) => {
            form.setFieldValue('patient_number', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Dropdown
        menu={{
          onClick: handleDropdownClick,
          items: addressDropdownMenu()
        }}
      >
        <Form.Item name="postcode" label={t('Postcode')} rules={[{ required: true }]}>
          <Search
            enterButton="Search"
            loading={fetchingPostcodeOptions}
            onSearch={(e) => searchPostcode(e)}
            onChange={(e) => {
              form.setFieldValue('postcode', sanitiseValueFromAngleBrackets(e.target.value));
            }}
          />
        </Form.Item>
      </Dropdown>

      <Form.Item name="address_line_1" label={t('Address line 1')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('address_line_1', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="address_line_2" label={t('Address line 2')}>
        <Input
          onChange={(e) => {
            form.setFieldValue('address_line_2', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="address_line_3" label={t('Address line 3')}>
        <Input
          onChange={(e) => {
            form.setFieldValue('address_line_3', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="town_or_city" label={t('Town/city')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('town_or_city', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="county" label={t('Region/county')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('county', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Form.Item name="country" label={t('Country')} rules={[{ required: true }]}>
        <Input
          onChange={(e) => {
            form.setFieldValue('country', sanitiseValueFromAngleBrackets(e.target.value));
          }}
        />
      </Form.Item>

      <Flex>
        <Button type="link" onClick={closeModal} data-testid="cancel">
          {t('Cancel')}
        </Button>
        <Tooltip placement="top" title="This will not submit the request" trigger={['hover', 'focus']}>
          <Button onClick={() => saveRequest()} data-testid="save">
            {t('Save')}
          </Button>
        </Tooltip>
        <Button type="primary" htmlType="submit" data-testid="submit">
          {t('Submit')}
        </Button>
      </Flex>
    </Form>
  );
};

export default AddDependantForm;
